import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { skipToken } from '@reduxjs/toolkit/query';
import {
  Box,
  Button,
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Tfoot,
  Th,
  Thead,
  Tr,
  useDisclosure,
} from '@chakra-ui/react';
import { accountApi } from '@netiva/classifieds-api';
import { parseQueryValue } from '@netiva/classifieds-common';
import {
  CodeTextarea,
  DescriptionList,
  DescriptionListItem,
  DescriptionListTerm,
  Loader,
  Pagination,
} from '@netiva/classifieds-ui';

import { useDate, usePagination } from '@/hooks';
import { ImportLogStatus } from './ImportLogStatus';
import { prettifyJson } from './utils';
import { RepeatIcon } from '@chakra-ui/icons';

const PageSize = 20;

export const ImportLogs: FC = () => {
  const { t } = useTranslation();
  const { formatDate, formatDuration } = useDate();

  const [selectedId, setSelectedId] = useState<number>();
  const detailsDrawer = useDisclosure({
    onClose: () => {
      setSelectedId(undefined);
    },
  });

  const [searchParams] = useSearchParams();
  const currentPage = parseQueryValue(searchParams.get('p'), parseInt) || 1;

  const {
    data: importExecutions,
    isLoading: isLoadingList,
    refetch,
  } = accountApi.useGetImportExecutions({
    pageIndex: currentPage - 1,
    pageSize: PageSize,
  });
  const { data: importExecution, isLoading: isLoadingDetail } = accountApi.useGetImportExecution(
    selectedId ? { id: selectedId } : skipToken
  );
  const { data: apiKeys, isLoading: isLoadingApiKeys } = accountApi.useGetApiKeys({ includeRevoked: true });
  const apiKeyMap = useMemo(() => (apiKeys ? new Map(apiKeys.apiKeys.map((k) => [k.uid, k.name])) : null), [apiKeys]);

  const paginationProps = usePagination({ totalPages: Math.ceil((importExecutions?.totalCount || 0) / PageSize) });

  const handleSelection = (id: number) => {
    setSelectedId(id);
    detailsDrawer.onOpen();
  };

  return (
    <Box position="relative">
      <Loader isLoading={isLoadingList} />
      <Flex>
        <Button leftIcon={<RepeatIcon />} onClick={refetch} ml="auto">
          {t('ui.buttons.refresh')}
        </Button>
      </Flex>
      <TableContainer>
        <Table size="sm">
          <Thead>
            <Tr>
              <Th>{t('api.importLogs.labels.status')}</Th>
              <Th>{t('api.importLogs.labels.startDate')}</Th>
              <Th>{t('api.importLogs.labels.endDate')}</Th>
              <Th>{t('api.importLogs.labels.duration')}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {importExecutions
              ? importExecutions.items.map((ex) => (
                  <Tr
                    key={ex.id}
                    cursor="pointer"
                    onClick={() => handleSelection(ex.id)}
                    bg={ex.id === selectedId ? 'neutral.200' : 'unset'}
                  >
                    <Td>
                      <ImportLogStatus execution={ex} />
                    </Td>
                    <Td>{formatDate(ex.startDate, true)}</Td>
                    <Td>{formatDate(ex.endDate, true)}</Td>
                    <Td>{formatDuration(ex.startDate, ex.endDate)}</Td>
                  </Tr>
                ))
              : null}
          </Tbody>
          <Tfoot>
            <Tr>
              <Td colSpan={5}>
                <Flex justify="center">
                  <Pagination {...paginationProps} />
                </Flex>
              </Td>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>

      <Drawer placement="right" size={['full', 'lg', 'lg', 'xl']} {...detailsDrawer}>
        <DrawerOverlay />
        <DrawerContent>
          {selectedId ? (
            <>
              <DrawerCloseButton />
              <DrawerHeader>
                {t('api.importLogs.details.title')} {selectedId}
              </DrawerHeader>

              <DrawerBody>
                <Skeleton isLoaded={!isLoadingDetail}>
                  {importExecution ? (
                    <DescriptionList background="transparent" padding={0}>
                      <DescriptionListTerm>{t('api.importLogs.labels.id')}</DescriptionListTerm>
                      <DescriptionListItem>{importExecution.id}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.status')}</DescriptionListTerm>
                      <DescriptionListItem>
                        <ImportLogStatus execution={importExecution} />
                      </DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.startDate')}</DescriptionListTerm>
                      <DescriptionListItem>{formatDate(importExecution.startDate, true)}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.endDate')}</DescriptionListTerm>
                      <DescriptionListItem>{formatDate(importExecution.endDate, true)}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.duration')}</DescriptionListTerm>
                      <DescriptionListItem>
                        {formatDuration(importExecution.startDate, importExecution.endDate)}
                      </DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.type')}</DescriptionListTerm>
                      <DescriptionListItem>{importExecution.type}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.mode')}</DescriptionListTerm>
                      <DescriptionListItem>{importExecution.mode}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.source')}</DescriptionListTerm>
                      <DescriptionListItem>{importExecution.source}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.requestVersion')}</DescriptionListTerm>
                      <DescriptionListItem>{importExecution.requestVersion}</DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.requestJson')}</DescriptionListTerm>
                      <DescriptionListItem>
                        <CodeTextarea value={prettifyJson(importExecution.requestJson)} />
                      </DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.resultJson')}</DescriptionListTerm>
                      <DescriptionListItem>
                        <CodeTextarea value={prettifyJson(importExecution.resultJson)} />
                      </DescriptionListItem>

                      <DescriptionListTerm>{t('api.importLogs.labels.apiKey')}</DescriptionListTerm>
                      <DescriptionListItem>
                        <Skeleton isLoaded={!isLoadingApiKeys}>
                          {(importExecution.apiKeyUid && apiKeyMap?.get(importExecution.apiKeyUid)) ||
                            importExecution.apiKeyUid}
                        </Skeleton>
                      </DescriptionListItem>
                    </DescriptionList>
                  ) : null}
                </Skeleton>
              </DrawerBody>

              <DrawerFooter>
                <Button variant="outline" onClick={detailsDrawer.onClose}>
                  {t('ui.buttons.close')}
                </Button>
              </DrawerFooter>
            </>
          ) : null}
        </DrawerContent>
      </Drawer>
    </Box>
  );
};
